/**
 * Schnell-Einstellungen im Blatt der Antwortlänge.
 * (29.09.2026, Ticket 2736 mit der Ergänzung aus 2737)
 *
 * Martin, 28.09.2026: Der Knopf in der Fußzeile bleibt schlank, erweitert
 * wird nur das Blatt, das er öffnet — *„für Einstellungen, die man mitten im
 * Gespräch schnell ändern will"*. Hauptwunsch: die Sprechgeschwindigkeit;
 * dazu die Emojis als Stufen aus / dezent / reichlich.
 *
 * 29.09.2026 (Ticket 2800): Das Tempo ist ein Regler mit vier Rasten, von
 * „Normal“ bis „Sehr schnell“ (1,6×) — Martin: *„Die heutigen Stufen sind
 * sehr moderat; ‚schnell‘ und ‚sehr schnell‘ sind für Vielhörer.“*
 * 30.09.2026 (Ticket 2800, Nachtrag): sieben Positionen von 1,0 bis 1,6 im
 * Schritt 0,1, die vier Namen bleiben bei 1,0 / 1,2 / 1,4 / 1,6 — Martin:
 * *„Schnell und Sehr schnell sind fast zu schnell; die Schritte … feiner
 * staffeln.“* Dazwischen zeigt die Zeile „1,1×“, „1,3×“, „1,5×“.
 *
 * 29.09.2026 (abends): dazu „Absenden nach Stille“ samt dem Stand von Auto —
 * Martin: *„Vielleicht kann man die Einstellung auch noch in das Feld
 * einbauen, wo die Kurz-, Mittel- und Lang-Ausgabe ist: ob der Auto-Modus an
 * sein soll oder nicht und inwieweit er jetzt schon aktiv ist.“* Die Zeile
 * steht in lib/stille-blatt-zeile.ts. Seit 22.10.2026 nur noch als Anzeige
 * mit dem Weg in die Einstellungen — dort und nur dort wird gewählt (Martin:
 * „verstehe ich nicht, weil wir in den Einstellungen doch etwas anderes haben“).
 *
 * ⚠️ EIN Besitzer je Einstellung: Tempo ist `speed`, Emojis sind
 *    `emojisInReplies`, die Stille ist `sendeStilleMs` — dieselben Felder,
 *    die die vollen Einstellungen schreiben (Regler „Vorlese-Tempo“, Karte
 *    „Emojis in Antworten“, Abschnitt „Von selbst absenden“). Hier
 *    gibt es keinen eigenen Zustand: gezeichnet wird bei jedem Mal aus dem
 *    Einstellungs-Zwischenspeicher, gespeichert über speichereEinstellungen.
 *
 * 21.10.2026 (Eingabe in einer Zeile, Entwurf A): „Mikro öffnet von selbst“
 * stand seit 19.10. als eigener Chip im Eingabefeld. Dort ist es jetzt nur
 * noch ein Punkt am Feld-Mikro; der Schalter zieht hierher, unter „Absenden
 * nach Stille“ — derselbe Zustand (`autoMikroNachFrage`), dieselben
 * Funktionen wie der Chip (lib/auto-mikro-chip.ts), kein zweiter Besitzer.
 *
 * Nicht hier (Maßstab aus dem Ticket: nur, was man im laufenden Gespräch
 * umstellt): Lautsprecher und „Getipptes vorlesen“ liegen schon in
 * Daumen-Reichweite (Fußzeile, Plus-Menü)
 * (die Stille im Mikrofon-Fenster ist seit 30.09.2026 nur noch eine Anzeige,
 * Ticket 2842; umgestellt wird sie hier und in der Fußzeile); Persona,
 * Human Design und Immer-aufzeichnen bleiben in den vollen Einstellungen. Ein zweiter Knopf für dieselbe Sache wäre nur
 * mehr zum Suchen.
 */
import {
  EMOJI_STUFEN,
  emojiStufe,
  emojiStufenName,
  type EmojiStufe,
  type EmojiStufenName,
} from "../../shared/emoji-stufe.js";
import { loadSettings, speichereEinstellungen, type VoiceV8Settings } from "./settings.js";
import { baueStilleZeile } from "./stille-blatt-zeile.js";
import { STILLE_ABSCHNITT } from "./stille-wahl.js";
import { baueStufenWahl } from "./stufen-wahl.js";
import { schalteAutoMikroChip, zeigeAutoMikroChip } from "./auto-mikro-chip.js";

/** Die Einstellungsseite, auf der „Von selbst absenden“ steht (settings-view.ts, buildSprechenSeite). */
export const STILLE_SEITE = "sprechen";

/**
 * Die sieben Positionen des Tempo-Reglers, von 1,0 bis 1,6 im Schritt 0,1.
 * Vier tragen einen Namen (Normal, Zügig, Schnell, Sehr schnell), die drei
 * dazwischen nur ihre Zahl („1,1×“, „1,3×“, „1,5×“). Werte aus dem Raster des
 * Reglers in den vollen Einstellungen (shared/vorlese-tempo.ts: 0,7–2,0 in
 * Schritten von 0,1) — beide schreiben `speed`, ein zweites Feld gibt es
 * nicht.
 *
 * ⚠️ 29.09.2026 (Ticket 2800): Bis hierher Langsam 0,85 / Normal 1,0 /
 *    Schnell 1,15 — 1,15, weil 1,2 am 05.07. für Martin „extrem schnell“
 *    klang. Heute nennt er genau diese Stufen „sehr moderat“. Die alten Werte
 *    rücken beim Lesen auf das Raster (1,15 → 1,2 = „Zügig“, 0,85 → 0,9);
 *    langsamer als normal geht weiter über den Regler in den vollen
 *    Einstellungen.
 *
 *    Oben 1,6, der Vorschlag aus dem Auftrag. Gemessen bleibt der Ton bis
 *    2,0 sauber (shared/vorlese-tempo.ts) — der Regler in den vollen
 *    Einstellungen reicht darum bis 2,0.
 *
 * ⚠️ 30.09.2026 (Ticket 2800, Nachtrag): die Namen bleiben, wo sie waren
 *    (1,0 / 1,2 / 1,4 / 1,6); neu sind die drei Zwischenwerte. Martin: Normal
 *    sei zu langsam, Schnell zu schnell — dazwischen fehlte ihm Feinheit.
 */
export const TEMPO_STUFEN: ReadonlyArray<{ wert: number; name: string | null }> = [
  { wert: 1, name: "Normal" },
  { wert: 1.1, name: null },
  { wert: 1.2, name: "Zügig" },
  { wert: 1.3, name: null },
  { wert: 1.4, name: "Schnell" },
  { wert: 1.5, name: null },
  { wert: 1.6, name: "Sehr schnell" },
];

/** So heißt jede Emoji-Stufe in der Oberfläche — hier und in den vollen Einstellungen. */
export const EMOJI_TEXT: Readonly<Record<EmojiStufenName, string>> = {
  aus: "Aus",
  dezent: "Dezent",
  reichlich: "Reichlich",
};

/**
 * Wann eine neue Emoji-Stufe wirkt.
 *
 * ⚠️ Die Regel steht im System-Prompt, und die warme Sitzung friert ihn
 *    beim Bau ein (server/chat.ts, EMOJI_ZEILE). Ohne diesen Satz sähe die
 *    nächste Antwort aus wie vorher — genau so entstand Ticket 747
 *    („Schalter steht auf an, ich habe aber noch kein einziges Emoji
 *    gesehen“).
 */
export const EMOJI_WANN = "Gilt ab einer der nächsten Antworten.";

/** Die Stufe, die zu diesem Tempo gehört — `null` bei einem eigenen Wert vom Regler. */
function tempoStufe(speed: number | undefined): number | null {
  const t = typeof speed === "number" ? speed : 1;
  return TEMPO_STUFEN.find((s) => Math.abs(s.wert - t) < 0.001)?.wert ?? null;
}

/** Die Zahl eines Tempos, wie man sie liest: „1,4“. */
function tempoZahl(speed: number): string {
  return speed.toLocaleString("de-AT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** „1,4×“ — wie man ein Tempo liest (Blatt und volle Einstellungen). */
export function tempoText(speed: number): string {
  return `${tempoZahl(speed)}×`;
}

/**
 * Der Tempo-Regler: sieben Rasten, darunter die Namen der vier benannten.
 *
 * Ein echter Regler (`input type="range"`) — mit Pfeiltasten und Vorleser
 * bedienbar, bei den benannten Rasten kommt der Name als aria-valuetext. Die
 * Namen darunter sind zusätzlich antippbar, denn in iOS springt ein Regler
 * nicht dorthin, wo man auf die Bahn tippt, man muss den Knopf ziehen; die
 * Zwischenwerte (1,1 / 1,3 / 1,5) erreicht man durch Ziehen oder mit den
 * Pfeiltasten, ihr kleiner Strich ist nur Markierung.
 *
 * Gespeichert wird beim Loslassen (`change`), nicht bei jedem Schritt
 * dazwischen; während des Ziehens zeigt die Zeile schon, wo man steht.
 */
function baueTempoRegler(
  wertAnzeige: HTMLElement,
  waehle: (wert: number) => void,
): { el: HTMLElement; zeige(speed: number): void } {
  const el = document.createElement("div");
  el.className = "tempo-regler";
  const feld = document.createElement("input");
  feld.type = "range";
  feld.className = "tempo-regler-feld";
  feld.min = "0";
  feld.max = String(TEMPO_STUFEN.length - 1);
  feld.step = "1";
  feld.setAttribute("aria-label", "Vorlese-Tempo");
  const marken = document.createElement("div");
  marken.className = "tempo-regler-marken";
  // Der Regler selbst trägt Name und Wert für Vorleser — die Marken sind
  // nur die sichtbare Beschriftung und der Tipp-Weg für den Finger.
  marken.setAttribute("aria-hidden", "true");
  // Je Raste ein Element, in der Reihenfolge der Rasten: mit Namen antippbar,
  // ohne Namen nur ein Strich (die CSS-Plätze hängen an dieser Reihenfolge).
  const rasten = TEMPO_STUFEN.map((s, i) => {
    const m = document.createElement("span");
    if (s.name === null) {
      m.className = "tempo-regler-strich";
    } else {
      m.className = "tempo-regler-marke";
      m.textContent = s.name;
      m.addEventListener("click", () => {
        feld.value = String(i);
        waehle(s.wert);
      });
    }
    marken.appendChild(m);
    return m;
  });
  el.append(feld, marken);

  /** Die Raste `i` als gewählt zeigen — Knopf, Name oder Strich darunter, Wert oben. */
  function markiere(i: number, eigen: number | null): void {
    feld.value = String(i);
    rasten.forEach((m, k) => m.toggleAttribute("data-an", k === i && eigen === null));
    const stufe = TEMPO_STUFEN[i];
    if (eigen === null) {
      delete el.dataset.eigen;
      wertAnzeige.textContent = tempoText(stufe.wert);
      feld.setAttribute(
        "aria-valuetext",
        stufe.name === null ? `${tempoZahl(stufe.wert)}-fach` : `${stufe.name}, ${tempoZahl(stufe.wert)}-fach`,
      );
    } else {
      // Ein Wert vom feinen Regler zwischen den Rasten: keine Raste wird
      // vorgetäuscht — der Knopf steht hohl bei der nächsten, oben der Wert.
      el.dataset.eigen = "";
      wertAnzeige.textContent = `eigenes: ${tempoText(eigen)}`;
      feld.setAttribute("aria-valuetext", `eigenes Tempo, ${tempoZahl(eigen)}-fach`);
    }
  }

  const raste = (): number => Number(feld.value);
  feld.addEventListener("input", () => markiere(raste(), null));
  feld.addEventListener("change", () => waehle(TEMPO_STUFEN[raste()].wert));

  return {
    el,
    zeige(speed) {
      const stufe = tempoStufe(speed);
      const naechste = TEMPO_STUFEN.reduce(
        (beste, s, i) => (Math.abs(s.wert - speed) < Math.abs(TEMPO_STUFEN[beste].wert - speed) ? i : beste),
        0,
      );
      markiere(naechste, stufe === null ? speed : null);
    },
  };
}

/** Der Satz unter dem Namen einer Gruppe: eine Zeile, was die Wahl bewirkt (20.10.2026, Blatt neu geordnet). */
function erklaerung(text: string): HTMLElement {
  const p = document.createElement("p");
  p.className = "schnell-einst-hinweis";
  p.textContent = text;
  return p;
}

/** Was die Wahl bewirkt — je Gruppe ein Satz. (Die Länge erklärt das Blatt selbst, antwort-tiefe-schalter.ts.) */
export const TEMPO_ERKLAERUNG = "Wie schnell Vespera vorliest.";
/** Der Satz zu „Mikro öffnet von selbst“ (der Punkt am Mikrofon im Eingabefeld zeigt „an“). */
export const MIKRO_AUTO_ERKLAERUNG = "Nach einer Frage geht das Mikro von selbst auf. Aus: nur Tippen.";

/** Eine Zeile im Blatt: Name (und Nebenwert) über der Stufenwahl. */
function zeile(name: string): { el: HTMLElement; kopf: HTMLElement } {
  const el = document.createElement("div");
  el.className = "schnell-einst-zeile";
  const kopf = document.createElement("div");
  kopf.className = "schnell-einst-kopf";
  const n = document.createElement("span");
  n.className = "schnell-einst-name";
  n.textContent = name;
  kopf.appendChild(n);
  el.appendChild(kopf);
  return { el, kopf };
}

/**
 * Der Bereich unter der Antwortlänge: Vorlese-Tempo, Emojis und Absenden nach
 * Stille. Ein Tippen speichert sofort; das Blatt bleibt offen (selectSheet,
 * `zusatz`).
 */
export function baueSchnellEinstellungen(): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "schnell-einst";

  const tempo = zeile("Vorlese-Tempo");
  const tempoWert = document.createElement("span");
  tempoWert.className = "schnell-einst-wert";
  tempo.kopf.appendChild(tempoWert);
  const tempoRegler = baueTempoRegler(tempoWert, (wert) => speichere({ speed: wert }));
  tempo.el.append(erklaerung(TEMPO_ERKLAERUNG), tempoRegler.el);

  const emojis = zeile("Emojis");
  const emojiWahl = baueStufenWahl<EmojiStufe>(
    "Emojis",
    EMOJI_STUFEN.map((s) => ({ wert: s, text: EMOJI_TEXT[emojiStufenName(s)] })),
    (wert) => speichere({ emojisInReplies: wert }),
  );
  emojis.el.appendChild(emojiWahl.el);
  emojis.el.appendChild(erklaerung(EMOJI_WANN));

  // Was nicht gespeichert werden konnte, steht hier — sichtbar statt still
  // (dieselben Worte wie überall, einstellungen-ausstehend.ts).
  const meldung = document.createElement("p");
  meldung.className = "schnell-einst-meldung";
  meldung.setAttribute("role", "status");

  // Stille (22.10.2026): nur Anzeige — gewählt wird allein in den Einstellungen (Sprechen → Von selbst absenden).
  // Der Weg schließt das Blatt über seinen eigenen Schließen-Knopf (selectSheet: Fokus und Stapel wie immer), dann die Seite.
  const stille = baueStilleZeile(() => {
    wrap.closest(".sheet-card")?.querySelector<HTMLButtonElement>(".close-btn--corner")?.click();
    // `fokus`: die Seite setzt den Fokus auf den Abschnitt — er bliebe sonst auf dem Auslöser im versteckten Chat.
    document.dispatchEvent(new CustomEvent("vespera:go-settings", { detail: { sub: STILLE_SEITE, fokus: STILLE_ABSCHNITT } }));
  });

  // „Mikro öffnet von selbst“ (21.10.2026): ein Schalter an/aus — Stand, Ansage und Titel setzt zeigeAutoMikroChip.
  const mikro = zeile("Mikro öffnet von selbst");
  const mikroSchalter = document.createElement("button");
  mikroSchalter.type = "button";
  mikroSchalter.className = "schnell-einst-schalter";
  const mikroWort = document.createElement("span");
  mikroWort.className = "composer-automikro-zustand";
  mikroSchalter.appendChild(mikroWort);
  mikroSchalter.addEventListener("click", () => {
    schalteAutoMikroChip(mikroSchalter, (text) => {
      meldung.textContent = text;
      delete meldung.dataset.art;
    });
  });
  mikro.kopf.appendChild(mikroSchalter);
  mikro.el.appendChild(erklaerung(MIKRO_AUTO_ERKLAERUNG));

  // Die Reihenfolge des Blatts: Länge (darüber) · Tempo · Absenden nach Stille · Mikro öffnet von selbst · Emojis.
  wrap.append(tempo.el, stille.el, mikro.el, emojis.el, meldung);

  function zeichne(): void {
    const s = loadSettings();
    tempoRegler.zeige(s.speed);
    emojiWahl.zeige(emojiStufe(s.emojisInReplies));
    stille.zeichne();
    zeigeAutoMikroChip(mikroSchalter);
  }

  function speichere(patch: Partial<VoiceV8Settings>): void {
    meldung.textContent = "";
    delete meldung.dataset.art;
    const antwort = speichereEinstellungen(patch);
    zeichne();
    // Nach der Antwort des Servers noch einmal: Hat ein anderes Gerät
    // später geändert, gilt dessen Wert — und genau der steht dann da.
    void antwort.then((e) => {
      zeichne();
      meldung.textContent = e.text;
      // Wie in den vollen Einstellungen (settings-view.ts, syncHandler):
      // „überholt“ ist ein Hinweis — der Wert des anderen Geräts steht
      // richtig da. Alles andere mit Text heißt „nicht gespeichert“.
      if (e.text) meldung.dataset.art = e.zustand === "ueberholt" ? "hinweis" : "fehler";
      else delete meldung.dataset.art;
    });
  }

  zeichne();
  return wrap;
}
